import { useRef, useState, useEffect, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

const LENGTH = 6;

/**
 * Segmented 6-box OTP entry — the redesign's "no generic form fields"
 * principle applied for real: signup and login used a single generic
 * `<Input>` for the 6-digit sign-in code until this (findings.md #5, the
 * clearest gap between what the Phase 0 preview approved and what shipped
 * — the component existed only in the throwaway /theme-preview route).
 *
 * Internally-stateful (a digit-array, not derived from `value` each
 * render) — simpler and more robust than trying to keep a padded/trimmed
 * string in sync with six independent boxes. `onChange` fires the joined
 * string up to the parent's existing `code`/`setCode` state on every edit;
 * `value` is read only to notice an external reset (parent clearing the
 * code after "wrong code" or navigating away and back). In practice the
 * parent step/form unmounts this component on those transitions anyway
 * (StepReveal's `key={step}` in the wizard; conditional render in login),
 * so boxes start empty naturally without fighting external state.
 */
export function OtpInput({
  id,
  value,
  onChange,
  autoFocus,
  disabled,
}: {
  /** Prefixes each box's own id (`${id}-0`..`${id}-5`) — pass the id a
   *  <Field label htmlFor> would have pointed at. */
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const [digits, setDigits] = useState<string[]>(() => {
    const seed = value.replace(/\D/g, '').slice(0, LENGTH).split('');
    return Array.from({ length: LENGTH }, (_, i) => seed[i] ?? '');
  });
  const [focused, setFocused] = useState<number | null>(null);
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const prevValue = useRef(value);

  // External reset (parent set value back to '') — clear the boxes to match.
  useEffect(() => {
    if (value === '' && prevValue.current !== '') {
      setDigits(Array(LENGTH).fill(''));
    }
    prevValue.current = value;
  }, [value]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
    // Only on mount — autoFocus shouldn't steal focus on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function commit(next: string[]) {
    setDigits(next);
    onChange(next.join(''));
  }

  function handleChange(i: number, raw: string) {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = digit;
    commit(next);
    if (digit && i < LENGTH - 1) refs.current[i + 1]?.focus();
  }

  function handleKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      const next = [...digits];
      next[i - 1] = '';
      commit(next);
      refs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < LENGTH - 1) refs.current[i + 1]?.focus();
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH).split('');
    if (!pasted.length) return;
    e.preventDefault();
    const next = Array.from({ length: LENGTH }, (_, i) => pasted[i] ?? '');
    commit(next);
    refs.current[Math.min(pasted.length, LENGTH - 1)]?.focus();
  }

  return (
    <div className="flex gap-2" role="group" aria-label="6-digit verification code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          id={`${id}-${i}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={d}
          disabled={disabled}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={() => setFocused(i)}
          onBlur={() => setFocused((f) => (f === i ? null : f))}
          className={cn(
            'h-12 w-10 rounded-[10px] border bg-white text-center text-lg font-mono tabular-nums text-breaker-ink outline-none transition-all duration-200 ease-out',
            'disabled:cursor-not-allowed disabled:bg-breaker-ground-2 disabled:text-breaker-mute',
            focused === i || d
              ? 'border-breaker-identity ring-2 ring-breaker-identity/20'
              : 'border-breaker-line-2',
          )}
        />
      ))}
    </div>
  );
}
